import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import BusinessLookup from './BusinessLookup';
import configService from '../services/configService';

function BusinessInfo({ onComplete }) {
  const { updateUserProfile, user } = useAuth();
  const [formData, setFormData] = useState({
    website: '',
    businessPhone: '',
    businessName: user?.attributes?.['custom:business_name'] || '',
    address: '',
    businessHours: null
  });
  const [loading, setLoading] = useState(false);
  const [showLookup, setShowLookup] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [initialFormData, setInitialFormData] = useState(null);
  
  // Load existing config data on mount
  useEffect(() => {
    const loadExistingConfig = async () => {
      try {
        const cognitoUserId = user?.attributes?.sub;
        const businessId = user?.attributes?.['custom:business_id'];
        
        if (cognitoUserId && businessId) {
          const config = await configService.getBusinessConfigByUserId(cognitoUserId);
          if (config) {
            const loadedData = {
              businessName: config.business_name || user?.attributes?.['custom:business_name'] || '',
              website: config.website || '',
              businessPhone: config.phone_number || '',
              address: config.address || '',
              businessHours: config.business_hours || null
            };
            setFormData(loadedData);
            setInitialFormData(loadedData);
          }
        } else {
          // Set initial data for new users
          const initialData = {
            businessName: user?.attributes?.['custom:business_name'] || '',
            website: '',
            businessPhone: '',
            address: '',
            businessHours: null
          };
          setInitialFormData(initialData);
        }
      } catch (error) {
        console.warn('Could not load existing config:', error);
      } finally {
        setInitialLoading(false);
      }
    };
    
    loadExistingConfig();
  }, [user]);

  const handleBusinessSelect = (businessData) => {
    console.log('Business data received:', businessData);
    
    const updatedData = {
      businessName: businessData.name || businessData.business_name || formData.businessName,
      website: businessData.website || businessData.url || formData.website,
      businessPhone: businessData.phone || businessData.phone_number || businessData.formatted_phone_number || formData.businessPhone,
      address: businessData.address || businessData.formatted_address || formData.address,
      businessHours: businessData.businessHours
    };
    
    console.log('Updated data:', updatedData);
    
    // Just populate the form and hide lookup - let user review before saving
    setFormData({
      ...formData,
      ...updatedData
    });
    setShowLookup(false);
  };

  const saveBusinessInfo = async () => {
    setLoading(true);
    
    try {
      console.log('💾 BusinessInfo: Saving business info');
      console.log('💾 BusinessInfo: Business Name:', formData.businessName);
      console.log('💾 BusinessInfo: Phone:', formData.businessPhone);
      console.log('💾 BusinessInfo: Website:', formData.website);
      console.log('💾 BusinessInfo: Address:', formData.address);
      
      // Validate required fields
      if (!formData.businessName || !formData.businessPhone || !formData.website || !formData.address) {
        throw new Error('Please fill in all required fields');
      }
      
      // Create business config using user ID
      const cognitoUserId = user.attributes.sub;
      
      // Check if config already exists to preserve business_description
      let existingConfig = null;
      try {
        existingConfig = await configService.getBusinessConfigByUserId(cognitoUserId);
      } catch (error) {
        // Config doesn't exist yet, that's fine
      }
      
      console.log('💾 BusinessInfo: Preserving existing description:', existingConfig?.business_description ? 'YES' : 'NO');
      console.log('💾 BusinessInfo: Preserving existing assistant_name:', existingConfig?.assistant_name ? 'YES' : 'NO');
      
      const configPayload = {
        business_name: formData.businessName,
        business_description: existingConfig?.business_description || `${formData.businessName} - A professional business`,
        phone_number: formData.businessPhone,
        website: formData.website,
        address: formData.address,
        business_hours: formData.businessHours,
        known_callers: {},
        tools: []
      };
      
      // Preserve assistant_name if it exists
      if (existingConfig?.assistant_name) {
        configPayload.assistant_name = existingConfig.assistant_name;
      }
      
      const result = await configService.createBusinessConfigByUserId(cognitoUserId, configPayload);
      
      console.log('✅ BusinessInfo: Config created successfully:', result);
      
      // Update initial form data to reflect saved state
      setInitialFormData({...formData});
      
      // Set flag for Step 2 to know this is a new config
      sessionStorage.setItem('isNewConfig', 'true');
      
      // Save business_id to Cognito profile
      if (result.business_id) {
        try {
          await updateUserProfile({
            'custom:business_id': result.business_id
          });
          console.log('✅ BusinessInfo: Business ID saved to Cognito:', result.business_id);
        } catch (profileError) {
          console.error('Failed to save business_id to Cognito:', profileError);
          // Continue anyway - business_id is stored in backend
        }
      }
      
      return true;
    } catch (error) {
      console.error('❌ BusinessInfo: Error saving:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const success = await saveBusinessInfo();
    if (success) {
      onComplete();
    }
  };

  // Track unsaved changes
  useEffect(() => {
    if (initialFormData && window.setHasUnsavedChanges) {
      const hasChanges = JSON.stringify(formData) !== JSON.stringify(initialFormData);
      window.setHasUnsavedChanges(hasChanges);
    }
  }, [formData, initialFormData]);

  // Expose save function to Setup.js
  useEffect(() => {
    window.saveBusinessInfo = saveBusinessInfo;
    return () => {
      delete window.saveBusinessInfo;
    };
  }, [formData, user]);

  if (initialLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <div style={{
          width: '32px',
          height: '32px',
          border: '3px solid #f3f4f6',
          borderTop: '3px solid #2563eb',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 1rem'
        }}></div>
        <p>Loading business information...</p>
      </div>
    );
  }
  
  if (showLookup) {
    return (
      <div>
        <BusinessLookup 
          onBusinessSelect={handleBusinessSelect}
          initialData={{ businessName: formData.businessName }}
        />

        <div style={{ textAlign: 'center', marginTop: '1rem' }}>
          <button
            onClick={() => setShowLookup(false)}
            style={{
              background: 'none',
              border: '1px solid #d1d5db',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            Enter Manually Instead
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      background: 'white',
      padding: '2rem',
      borderRadius: '12px',
      boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
      maxWidth: '500px',
      margin: '0 auto'
    }}>
      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <button
          onClick={() => setShowLookup(true)}
          style={{
            background: '#2563eb',
            color: 'white',
            border: 'none',
            padding: '0.75rem 1.5rem',
            borderRadius: '6px',
            fontSize: '1rem',
            cursor: 'pointer',
            marginBottom: '1rem'
          }}
        >
          🔍 Find My Business on Google Maps
        </button>
        <p style={{ fontSize: '0.9rem', color: '#6b7280' }}>or enter details manually below</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
            Business Name
          </label>
          <input
            type="text"
            value={formData.businessName}
            onChange={(e) => setFormData({...formData, businessName: e.target.value})}
            placeholder="Your Business Name"
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '1rem'
            }}
            required
          />
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
            Website URL
          </label>
          <input
            type="url"
            value={formData.website}
            onChange={(e) => setFormData({...formData, website: e.target.value})}
            placeholder="https://yourbusiness.com"
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '1rem'
            }}
            required
          />
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
            Business Phone Number
          </label>
          <input
            type="tel"
            value={formData.businessPhone}
            onChange={(e) => setFormData({...formData, businessPhone: e.target.value})}
            placeholder="(555) 123-4567"
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '1rem'
            }}
            required
          />
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
            Business Address
          </label>
          <input
            type="text"
            value={formData.address}
            onChange={(e) => setFormData({...formData, address: e.target.value})}
            placeholder="123 Main St, City, State 12345"
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '1rem'
            }}
            required
          />
        </div>
        {formData.businessHours && (
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Business Hours
            </label>
            <textarea
              value={formData.businessHours.weekday_text ? formData.businessHours.weekday_text.join('\n') : ''}
              onChange={(e) => {
                const lines = e.target.value.split('\n').filter(line => line.trim());
                setFormData({
                  ...formData, 
                  businessHours: {
                    ...formData.businessHours,
                    weekday_text: lines
                  }
                });
              }}
              rows={7}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '1rem',
                fontFamily: 'inherit',
                resize: 'vertical'
              }}
            />
          </div>
        )}
        <div style={{ marginBottom: '2rem' }}></div>

      </form>
    </div>
  );
}

export default BusinessInfo;